<script setup lang="ts">
  import { computed } from 'vue';
  import { useMatrixFillLabContext } from '../../model/useMatrixFillLab';
  import FieldRef from './FieldRef.vue';
  import LabSection from './LabSection.vue';
  import PermissionTrace from './PermissionTrace.vue';
  import RuleTrace from './RuleTrace.vue';

  const lab = useMatrixFillLabContext();

  const statCards = computed(() => {
    const stats = lab.stats.value;
    if (!stats) return [];
    return [
      { label: 'campos', value: stats.total, alert: false },
      { label: 'ocultos', value: stats.hidden, alert: false },
      { label: 'desabilitados', value: stats.disabled, alert: false },
      { label: 'sem permissão', value: stats.locked, alert: false },
      { label: 'tipos sem motor', value: stats.unsupported, alert: stats.unsupported > 0 },
      { label: 'erros', value: stats.issues, alert: stats.issues > 0 },
    ];
  });

  const compileTime = computed(() => {
    const ms = lab.compileTimeMs.value;
    return ms < 0.05 ? 'menos de 0,1 ms' : `${ms.toFixed(1).replace('.', ',')} ms`;
  });

  const issues = computed(() => lab.compiled.value?.issues ?? []);
</script>

<template>
  <LabSection
    title="Saída: o que o compilador decidiu"
    open
    :badge="`compilado em ${compileTime}`"
    hint="compileMatrix roda de novo a cada tecla, cargo ou regra e devolve a árvore que a folha desenha. Estes números são lidos dessa árvore, não da tela."
    data-testid="lab-output"
  >
    <div class="grid grid-cols-3 gap-2">
      <div
        v-for="card in statCards"
        :key="card.label"
        class="rounded-md border border-[var(--color-gray-02)] bg-[var(--color-white-02)] p-2 text-center"
      >
        <div
          class="text-lg font-semibold"
          :class="{ 'text-red-600': card.alert }"
        >
          {{ card.value }}
        </div>
        {{ card.label }}
      </div>
    </div>

    <div data-testid="lab-issues">
      <p
        v-if="issues.length === 0"
        class="text-green-700"
      >
        Nenhum erro de validação. O salvar está liberado.
      </p>
      <template v-else>
        <p class="mb-1 font-semibold text-red-700">
          {{ issues.length }} erro(s) que bloqueiam o salvar
        </p>
        <ul class="flex flex-col gap-1">
          <li
            v-for="issue in issues"
            :key="`${issue.fieldId}-${issue.code}`"
            class="flex items-center gap-2 rounded-md bg-red-50 px-2 py-1 text-red-800"
          >
            <FieldRef :id="issue.fieldId" />
            <span class="ml-auto shrink-0">
              <code class="text-[10px]">{{ issue.code }}</code>
              {{ issue.message }}
            </span>
          </li>
        </ul>
      </template>
    </div>

    <RuleTrace />
    <PermissionTrace />
  </LabSection>
</template>
